import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { action, computed } from 'mobx'
import { type WorkspacePostingOpportunity } from './WorkspacePostingOpportunity'
import { WorkspacePostingOpportunityViewModel } from './WorkspacePostingOpportunityViewModel'
import { WorkspacePostingOpportunitiesSearchViewModel } from './WorkspacePostingOpportunitiesSearchViewModel'
import { WorkspaceOpportunityDetailsViewModel } from './WorkspaceOpportunityDetailsViewModel'
import type {
  AlertErrorHandlerFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import { type WorkspacePostingOpportunityStore } from './WorkspacePostingOpportunityStore'
import { ViewModelList } from '@/infra/view-model-list'
import type { GetCurrentUserWorkspaceMembershipsFn } from '@/features/iam'

/**
 * View model for the outsourcing posts tab.
 */
export class WorkspacePostingOpportunitiesViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Workspace Opportunities view model list.
   */
  private readonly _opportunityVms: ViewModelList<
    WorkspacePostingOpportunity,
    WorkspacePostingOpportunityViewModel
  >

  /**
   * Opportunity searcher.
   */
  readonly searcher

  /**
   * Details of the selected opportunity.
   */
  readonly detailsVm

  constructor(
    private readonly _workspacePostingOpportunityStore: WorkspacePostingOpportunityStore,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
  ) {
    this.searcher = new WorkspacePostingOpportunitiesSearchViewModel(
      (query, signal) =>
        this._workspacePostingOpportunityStore.listOpportunities(query, signal),
      this._createAlertRetryWrapper,
    )

    this.detailsVm = new WorkspaceOpportunityDetailsViewModel(
      this._workspacePostingOpportunityStore,
      this._createAlertErrorHandler,
    )

    this._opportunityVms = new ViewModelList({
      models: () => this.searcher.scroller.items.slice(),
      create: (model) =>
        new WorkspacePostingOpportunityViewModel(
          model,
          () => this.detailsVm.disabled,
          () => this.detailsVm.selected,
          this.detailsVm.select,
        ),
    })

    this._lifecycle.manage(this.searcher)
    this._lifecycle.manage(this._opportunityVms)
  }

  /**
   * The postings the user can apply to or has an application in review for,
   * outside the workspaces they're already a member of.
   */
  @computed
  get opportunities() {
    const memberWorkspaceIds = new Set(
      this._getCurrentUserWorkspaceMemberships().map((m) => m.workspace.id),
    )
    return this._opportunityVms.items.filter(
      (vm) =>
        (vm.opportunity.status === 'Active' ||
          (vm.opportunity.status === 'InReview' &&
            vm.opportunity.isApplicant)) &&
        !memberWorkspaceIds.has(vm.opportunity.workspaceId),
    )
  }

  /**
   * Whether the list is empty after loading everything.
   */
  @computed
  get showEmptyState() {
    return (
      !this.searcher.scroller.fetch.pending &&
      this.opportunities.length === 0 &&
      this.searcher.scroller.isDone
    )
  }

  /** @inheritDoc */
  @action.bound
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  @action.bound
  deactivate() {
    // Deselect any selected op when leaving the Ops view.
    this.detailsVm.deselect()
    this._lifecycle.deactivate()
  }
}
